import useSession from "Hooks/session.hook";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";
import { fetchCustomerList } from "Services/customer.service";
import {
  listVehicles,
  listVehicleTypes,
  listServicePoints,
} from "Services/delivery-settings.services";
import { fetchDriverList } from "Services/new-driver-management.services";
import { DriverApprovalStatus, SortingMode } from "Types/new_driver_types";
import { useState } from "react";
import { useRecoilState } from "recoil";
import { isEmpty, map, omit, isNil } from "lodash";
import { fetchOrderStatisticsCount } from "Services/order.service";
import useCorporateType from "Hooks/corporate-type.hook";
import { toNum } from "Helpers/get-to-num";
import { toaster } from "Helpers/toaster";
import {
  listPickUp,
  listDropOff,
  updateAssignDriver,
  createCombinePickUp,
  updatePickUpStatus,
} from "Services/delivery.services";
import { useTranslation } from "react-i18next";
import usePagination from "Hooks/pagination.hook";
import { orderAtom } from "Recoil/order.atom";
import useCheckUserRole from "Hooks/check-user-role.hook";
import { UserRoleToAssign } from "Types/user.types";

const useParcelTrackingFilters = (props: any) => {
  const { tabKey, isCustomer } = props || {};

  const { org, roleTypes } = useSession();
  const { t } = useTranslation();
  const { isRole } = useCheckUserRole();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<any>([]);
  const [vehicleOptions, setVehicleOptions] = useState<any>([]);
  const [customerOptions, setCustomerOptions] = useState<any>([]);
  const [driverOptions, setDriverOptions] = useState<any>([]);
  const [sortingHubOptions, setSortingHubOptions] = useState<any>([]);
  const [globalFilterState, setGlobalFilterState] = useRecoilState(GlobalFilterAtom);
  const [currentListCount, setCurrentListCount] = useState(0);
  const [allOrderList, setAllOrderList] = useState<any>([]);
  const [orderState, setOrderState] = useRecoilState<any>(orderAtom);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [tabActiveKey, setTabActiveKey] = useState(tabKey);

  const { paginationComponent, itemPerPage, queryParams, setQueryParams } = usePagination({
    currentListCount,
  });

  const initialFilters = {
    orgID: "",
    ancestorOrgIDs: globalFilterState?.selectedOrgIDs?.length
      ? globalFilterState?.selectedOrgIDs
      : null,
  };

  const [filterObj, setFilterObj] = useState<any>(initialFilters);

  //   Statistics
  // ====================================================
  const statisticsBoxes = !isRole([UserRoleToAssign.PUBLIC_TRANSPORT_OPERATIONAL_USER]) ? [
    { key: "toPickup", name: "toPickUp", tab: "0", count: 0, iconSrc: "/assets/icons/truck.svg" },
    // { key: "dispatchReady", name: "dispatchReady", tab: "1", count: 0 },
    {
      key: "toReceive",
      name: "toReceive",
      tab: "1",
      count: 0,
      iconSrc: "/assets/icons/receive.svg",
    },
    {
      key: "inMidMile",
      name: "inMidMile",
      tab: "3",
      count: 0,
      iconSrc: "/assets/icons/midmile.svg",
    },
    {
      key: "inLastMile",
      name: "inLastMile",
      tab: "4",
      count: 0,
      iconSrc: "/assets/icons/lastmile.svg",
    },
    {
      key: "completed",
      name: "trackingCompleted",
      tab: roleTypes == "CUSTOMER-SUPER_ADMIN" ? "3" : "5",
      count: 0,
      iconSrc: "/assets/icons/completed.svg",
    },
  ] : [
    {
      key: "inMidMile",
      name: "inMidMile",
      tab: "0",
      count: 0,
      iconSrc: "/assets/icons/midmile.svg",
    },
  ];

  const [parcelStatistics, setParcelStatistics] = useState<any>(statisticsBoxes);

  const prepareFetch = () => {
    setIsLoading && setIsLoading(true);
  };

  const handleError = (errorMessage) => {
    console.log(errorMessage);
    setIsLoading && setIsLoading(false);
  };

  const handleListingVehicles = async () => {
    prepareFetch();
    let apiFilter = { active: true };
    // globalFilterState.selectedOrgIDs?.length ? apiFilter.ancestorOrgIDs = globalFilterState.selectedOrgIDs : null
    const responseData = await listVehicles(apiFilter);

    if (!isEmpty(responseData) && !responseData.error) {
      setVehicleOptions(responseData);
    } else {
      handleError("Failed to fetch vehicles.");
    }

    setIsLoading && setIsLoading(false);
  };

  const handleListingVehiclesTypes = async () => {
    prepareFetch();
    let apiFilter = { active: true };

    const responseData = await listVehicleTypes();

    if (!isEmpty(responseData) && !responseData.error) {
      setVehicleTypeOptions(responseData);
    } else {
      handleError("Failed to fetch vehicles.");
    }

    setIsLoading && setIsLoading(false);
  };

  const handleListingCustomers = async () => {
    prepareFetch();
    let orgIDs = [];
    orgIDs = globalFilterState.selectedOrgIDs?.length
      ? globalFilterState.selectedOrgIDs
      : [org._id];

    const responseData = await fetchCustomerList({}, orgIDs);

    if (!isEmpty(responseData) && !responseData.error) {
      setCustomerOptions(responseData);
    } else {
      setCustomerOptions([]);
      handleError("Failed to fetch customers.");
    }

    setIsLoading && setIsLoading(false);
  };

  const handleListingInternalDrivers = async () => {
    prepareFetch();
    let orgIDs = [];
    orgIDs = globalFilterState.selectedOrgIDs?.length
      ? globalFilterState.selectedOrgIDs
      : [org._id];

    const responseData = await fetchDriverList(
      { riderType: "INTERNAL" },
      orgIDs,
      DriverApprovalStatus.APPROVED,
    );

    if (!isEmpty(responseData) && !responseData.error) {
      setDriverOptions(responseData);
    } else {
      setDriverOptions([]);
      handleError("Failed to fetch drivers.");
    }

    setIsLoading && setIsLoading(false);
  };

  const handleListingSortingHub = async () => {
    prepareFetch();
    const responseData = await listServicePoints({active: true});

    if (!isEmpty(responseData) && !responseData.error) {
      setSortingHubOptions(responseData);
    } else {
      handleError("Failed to fetch origin hubs.");
    }

    setIsLoading && setIsLoading(false);
  };

  const getStatCount = async () => {
    let orgIDs = filterObj.ancestorOrgIDs?.length ? filterObj.ancestorOrgIDs : [org._id];
    const response = await fetchOrderStatisticsCount(orgIDs);
    if (response.statistics) {
      //map through the parcelStatistics + set count
      let parcelStat = map(parcelStatistics, (statObj) => {
        statObj.count = response.statistics[statObj.key];
        return statObj;
      });
      setParcelStatistics(parcelStat);
    }
  };

  const handleListingPickUpOrders = async () => {
    // orgID refers to customerID, not the user currently logged in
    // -> isCustomer is for when a customer logs in instead of company super admin
    let filters = {
      ...filterObj,
      status: ["OPEN", "PICKUP_IN_PROGRESS"],
      orgID: isCustomer ? org._id : filterObj.orgID,
    };

    filters = filterObj.orgID ? omit(filters, ["ancestorOrgIDs"]) : filters;
    const responseData = await listPickUp(
      filters,
      { offset: queryParams.offset, limit: toNum(itemPerPage) },
      { sortBy: "pickupCode", mode: SortingMode.ASC },
    );

    if (responseData && !responseData.error) {
      setCurrentListCount(responseData.length)
      setAllOrderList(responseData);
    } else {
      toaster("error", t("error.badRequest"));
    }
  };

  const handleListingDropOffOrders = async () => {
    // orgID refers to customerID, not the user currently logged in
    let filters = {
      ...filterObj,
      status: ["OPEN"],
      orgID: isCustomer ? org._id : filterObj.orgID,
    };
    filters = filterObj.orgID ? omit(filters, ["ancestorOrgIDs"]) : filters;

    const responseData = await listDropOff(
      filters,
      { offset: queryParams.offset, limit: toNum(itemPerPage) },
      { sortBy: "dropOffCode", mode: SortingMode.ASC },
    );

    if (responseData && !responseData.error) {
      setCurrentListCount(responseData.length)
      setAllOrderList(responseData);
    } else {
      // toaster("error", t("error.badRequest"));
      console.log("Failed to fetch dropoff orders.");
    }
  };

  const handleAssignDriver = async (inputValue) => {
    const responseData = await updateAssignDriver(inputValue);

    if (!isNil(responseData) && responseData.updated) {
      toaster("success", "Assigned Driver successfully");
      handleRefreshData();
    } else {
      toaster("error", "Failed to update");
    }
  };

  const handleCombinePickUpTask = async (inputValue) => {
    const responseData = await createCombinePickUp(inputValue);

    if (responseData.success == true) {
      toaster("success", "Combined Task successfully");
      handleRefreshData();
    } else {
      toaster("error", "Failed to update");
    }
  };

  const handleStatusUpdate = async (status, pickupID) => {
    const responseData = await updatePickUpStatus(pickupID, status);

    if (!isNil(responseData) && responseData.updated) {
      toaster("success", "Pick Up Status updated successfully");
      handleRefreshData();
    } else {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: "error" },
      });
      toaster("error", "Failed to update");
    }
  };

  //   Page Functions
  // ====================================================
  const handleRefreshData = () => {
    setFilterObj(initialFilters);
    setAllOrderList([]);
    handleAPICallsByTabs();
    // handleAPIListingByTabs();
    getStatCount();
  };

  const handleAPIListingByTabs = () => {
    switch (tabKey) {
      case "0":
        //DIOLKO PICKUP
        handleListingPickUpOrders();

        break;
      case "1":
        //CUSTOMER DROP OFF
        handleListingDropOffOrders();
        break;
    }
  };

  const handleAPICallsByTabs = () => {
    switch (tabKey) {
      case "0":
        //DIOLKO PICKUP
        handleListingVehiclesTypes();
        handleListingVehicles();
        handleListingCustomers();
        handleListingInternalDrivers();
        break;
      case "1":
        //CUSTOMER DROP OFF
        handleListingVehicles();
        break;
      case "2":
        //SORTING
        handleListingCustomers();
        handleListingVehicles();
        handleListingSortingHub();
        break;
      case "4":
        //LAST MILE
        handleListingVehicles();
        handleListingSortingHub();
        break;
      case "5":
        handleListingSortingHub();
        break;
    }
  };

  return {
    parcelStates: {
      initialFilters,
      statisticsBoxes,
      filterObj,
      setFilterObj,
      vehicleOptions,
      customerOptions,
      driverOptions,
      sortingHubOptions,
      vehicleTypeOptions,
      setVehicleTypeOptions,
      parcelStatistics,
      setParcelStatistics,
      allOrderList,
      setAllOrderList,
      orderState,
      setOrderState,
      selectedRows,
      setSelectedRows,
      tabActiveKey,
      setTabActiveKey,
    },
    parcelFunctions: {
      getStatCount,
      handleListingCustomers,
      handleListingInternalDrivers,
      handleListingSortingHub,
      handleListingVehicles,
      handleListingVehiclesTypes,
      handleAssignDriver,
      handleCombinePickUpTask,
      handleStatusUpdate,
      handleRefreshData,
      handleAPIListingByTabs,
      handleAPICallsByTabs,
    },
    loadingMechanism: {
      isLoading,
      setIsLoading,
    },
    paginationProps: {
      paginationComponent,
      itemPerPage,
      queryParams,
      setQueryParams,
      setCurrentListCount
    },
  };
};

export default useParcelTrackingFilters;
